import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/cn';

// Original app button language (sprint 7): rounded-lg, press scale, regular weight except filled
// buttons. The focus ring is kept for accessibility (the original had none).
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm ring-offset-background transition-all duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary font-medium text-primary-foreground hover:bg-primary-hover',
        destructive: 'bg-destructive font-medium text-destructive-foreground hover:bg-destructive/90',
        // original "bordered ghost": transparent, secondary text
        outline: 'border border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
        // original dashboard secondary
        secondary: 'border border-border bg-card text-foreground hover:bg-accent',
        // original .btn-ghost
        ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
        link: 'text-link underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3',
        xs: 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
        lg: 'h-10 px-8',
        icon: 'h-9 w-9',
        'icon-sm': 'h-7 w-7 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
Button.displayName = 'Button';

export { Button, buttonVariants };
